import React from 'react';
import { toast } from 'react-toastify';
import { checkNetworkHealth } from '../../services/systemService';

class NetworkStatus extends React.Component {
  intervalId = null;

  state = {
    isChecking: false,
    isBrowserOnline: typeof navigator === "undefined" ? true : navigator.onLine,
    isApiReachable: null,
    lastChecked: null,
  };

  componentDidMount() {
    this.checkHealth({ silent: true });
    this.intervalId = setInterval(() => this.checkHealth({ silent: true }), 15000);
    window.addEventListener("online", this.handleNetworkChange);
    window.addEventListener("offline", this.handleNetworkChange);
  }

  componentWillUnmount() {
    clearInterval(this.intervalId);
    window.removeEventListener("online", this.handleNetworkChange);
    window.removeEventListener("offline", this.handleNetworkChange);
  }

  checkHealth = async ({ silent = false } = {}) => {
    if (!navigator.onLine) {
      this.setState({
        isChecking: false,
        isBrowserOnline: false,
        isApiReachable: false,
        lastChecked: new Date(),
      });
      if (!silent) toast.error("Network connection lost.");
      return;
    }

    this.setState({ isChecking: true, isBrowserOnline: true });

    try {
      const { data: response } = await checkNetworkHealth();
      const isApiReachable = response === true || response?.status === true || response?.ok === true;

      this.setState({
        isChecking: false,
        isBrowserOnline: true,
        isApiReachable,
        lastChecked: new Date(),
      });

      if (!silent) {
        isApiReachable
          ? toast.success("Connection verified.")
          : toast.warning("API health check did not pass.");
      }
    } catch (error) {
      console.error('Error checking network health:', error);
      this.setState({
        isChecking: false,
        isBrowserOnline: true,
        isApiReachable: false,
        lastChecked: new Date(),
      });
      if (!silent) toast.error("API is unreachable.");
    }
  };

  handleNetworkChange = async () => {
    if (navigator.onLine) {
      toast.info("Network connection restored. Checking API...");
      await this.checkHealth({ silent: true });
    } else {
      this.setState({
        isChecking: false,
        isBrowserOnline: false,
        isApiReachable: false,
        lastChecked: new Date(),
      });
      toast.error("Network connection lost.");
    }
  };

  getStatusMeta() {
    const { isChecking, isBrowserOnline, isApiReachable, lastChecked } = this.state;
    const checked = lastChecked ? ` Last checked ${lastChecked.toLocaleTimeString()}.` : "";

    if (isChecking) {
      return {
        icon: "fa fa-refresh fa-spin",
        color: "text-info",
        label: `Checking connection...${checked}`,
      };
    }

    if (!isBrowserOnline) {
      return {
        icon: "fa fa-plug",
        color: "text-danger",
        label: `Browser is offline.${checked}`,
      };
    }

    if (isApiReachable) {
      return {
        icon: "fa fa-podcast",
        color: "text-success",
        label: `Connected to MBOSS API.${checked}`,
      };
    }

    return {
      icon: "fa fa-exclamation-triangle",
      color: "text-warning",
      label: `Browser is online, but MBOSS API is unreachable.${checked}`,
    };
  }

  render() {
    const status = this.getStatusMeta();
    return (
      <button
        type="button"
        className="network-status-button"
        onClick={() => this.checkHealth()}
        title={status.label}
        aria-label={status.label}
      >
        <i className={`${status.icon} navbar-icon ${status.color}`} />
      </button>
    );
  }
}

export default NetworkStatus;
